class CoffeeQuiz extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-coffee-quiz-dialog]');
    this.overlay = this.querySelector('.coffee-quiz__overlay');
    this.closeButtons = this.querySelectorAll('[data-coffee-quiz-close]');
    this.methodCardsContainer = this.querySelector('.coffee-quiz__cards--methods');
    this.methodButtons = this.querySelectorAll('[data-coffee-quiz-method]');
    this.methodLabels = this.querySelectorAll('[data-coffee-quiz-method-label]');
    this.strengthLabels = this.querySelectorAll('[data-coffee-quiz-strength-label]');
    this.tasteLabels = this.querySelectorAll('[data-coffee-quiz-taste-label]');
    this.steps = this.querySelectorAll('[data-coffee-quiz-step]');
    this.resultsContainer = this.querySelector('[data-coffee-quiz-results]');
    this.emptyResultMessage = this.querySelector('[data-coffee-quiz-empty]');
    this.resultProducts = this.querySelectorAll('[data-coffee-quiz-product]');
    this.pendingAddVariantId = '';
    this.selected = {
      method: this.dataset.defaultMethod || '',
      strength: '',
      taste: '',
    };

    this.addEventListener('click', this.onQuizClick.bind(this));
    this.addEventListener('change', this.onQuizChange.bind(this));
    this.addEventListener('submit', this.onQuizSubmit.bind(this), true);
    this.closeButtons.forEach((button) => {
      if (button === this.overlay) return;
      button.addEventListener('click', () => this.close('close_button'));
    });
    if (this.overlay) {
      this.overlay.addEventListener('click', (event) => {
        if (event.target !== this.overlay) return;
        event.stopPropagation();
        this.close('outside_click');
      });
    }
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close('escape');
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this));
    }

    this.syncMethodsFromFinder();
    this.emitQuizEvent('ready');
  }

  disconnectedCallback() {
    if (typeof this.cartUpdateUnsubscriber === 'function') this.cartUpdateUnsubscriber();
    if (typeof this.cartErrorUnsubscriber === 'function') this.cartErrorUnsubscriber();
  }

  open(method, trigger = {}) {
    this.previouslyFocused = document.activeElement;
    const selectedMethod = this.resolveMethod(method);
    this.selected.method = selectedMethod || this.dataset.defaultMethod || '';
    this.selected.strength = '';
    this.selected.taste = '';
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    this.emitQuizEvent('open', {
      method: this.selected.method,
      startsAtStep: selectedMethod ? 'strength' : 'method',
      trigger,
    });
    this.showStep(selectedMethod ? 'strength' : 'method', { reason: 'open' });
    requestAnimationFrame(() => {
      const closeButton = this.querySelector('.coffee-quiz__close');
      const focusTarget = closeButton || this.dialog;
      if (typeof trapFocus === 'function' && this.dialog && focusTarget) {
        trapFocus(this.dialog, focusTarget);
      } else if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus();
      }
    });
  }

  close(reason = 'manual') {
    const focusTarget =
      this.previouslyFocused && typeof this.previouslyFocused.focus === 'function' ? this.previouslyFocused : null;
    const wasOpen = this.classList.contains('is-open');
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    if (wasOpen) {
      this.emitQuizEvent('close', { reason });
    }

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(focusTarget);
    } else if (focusTarget) {
      focusTarget.focus();
    }
  }

  onQuizChange(event) {
    const sizeInput = event.target.closest('[data-coffee-quiz-size-input]');
    if (sizeInput) {
      const productCard = sizeInput.closest('[data-coffee-quiz-product]');
      if (!productCard) return;

      const variants = parseQuizVariants(productCard.dataset.coffeeQuizVariants);
      const selectedGrind = getQuizSelectedGrind(productCard) || this.selected.method || this.dataset.defaultMethod || '';
      const variant = findQuizVariant(variants, selectedGrind, sizeInput.value);
      this.updateProductCard(productCard, variant ? variant.id : '');
      this.emitQuizEvent('variant_change', {
        source: 'size',
        size: sizeInput.value,
        grind: selectedGrind,
        product: this.getProductEventData(productCard),
        variant: this.getVariantEventData(variant),
      });
      return;
    }

    const grindSelect = event.target.closest('[data-coffee-quiz-grind-select]');
    if (grindSelect) {
      const productCard = grindSelect.closest('[data-coffee-quiz-product]');
      if (!productCard) return;

      productCard.dataset.coffeeQuizGrindChosen = 'true';
      this.setGrindValidationState(productCard, false);
      const variants = parseQuizVariants(productCard.dataset.coffeeQuizVariants);
      const selectedSize = getQuizSelectedSize(productCard);
      const variant = findQuizVariant(variants, grindSelect.value, selectedSize);
      this.updateProductCard(productCard, variant ? variant.id : '');
      this.emitQuizEvent('variant_change', {
        source: 'grind',
        size: selectedSize,
        grind: grindSelect.value,
        product: this.getProductEventData(productCard),
        variant: this.getVariantEventData(variant),
      });
      return;
    }

    const variantSelect = event.target.closest('[data-coffee-quiz-variant-select]');
    if (!variantSelect) return;

    const productCard = variantSelect.closest('[data-coffee-quiz-product]');
    if (!productCard) return;

    this.updateProductCard(productCard, variantSelect.value);
    this.emitQuizEvent('variant_change', {
      source: 'select',
      product: this.getProductEventData(productCard),
      variant: this.getVariantEventData(findVariantById(parseQuizVariants(productCard.dataset.coffeeQuizVariants), variantSelect.value)),
    });
  }

  onQuizSubmit(event) {
    const form = event.target.closest('.coffee-quiz-product__form-inner');
    if (!form || !this.contains(form)) return;

    const productCard = form.closest('[data-coffee-quiz-product]');
    const grindControl = productCard?.querySelector('[data-coffee-quiz-grind-control]');
    const needsGrindSelection = Boolean(
      grindControl && !grindControl.hidden && productCard.dataset.coffeeQuizGrindChosen !== 'true'
    );

    if (needsGrindSelection) {
      event.preventDefault();
      this.pendingAddVariantId = '';
      this.setGrindValidationState(productCard, true);
      productCard.querySelector('[data-coffee-quiz-grind-select]')?.focus();
      return;
    }

    const formData = new FormData(form);
    this.pendingAddVariantId = String(formData.get('id') || '');
    this.emitQuizEvent('add_to_cart_submit', {
      product: productCard ? this.getProductEventData(productCard) : null,
      variantId: this.pendingAddVariantId,
    });
  }

  onCartUpdate(event) {
    if (!this.pendingAddVariantId || !event || event.source !== 'product-form') return;

    const addedVariantId = String(event.productVariantId || '');
    if (addedVariantId && addedVariantId !== this.pendingAddVariantId) return;

    this.pendingAddVariantId = '';
    this.emitQuizEvent('add_to_cart_success', {
      variantId: addedVariantId || '',
    });
    this.close('add_to_cart_success');
  }

  onCartError(event) {
    if (!this.pendingAddVariantId || !event || event.source !== 'product-form') return;

    const erroredVariantId = String(event.productVariantId || '');
    if (!erroredVariantId || erroredVariantId === this.pendingAddVariantId) {
      this.emitQuizEvent('add_to_cart_error', {
        variantId: this.pendingAddVariantId,
      });
      this.pendingAddVariantId = '';
    }
  }

  onQuizClick(event) {
    if (event.target === this || event.target === this.overlay) {
      this.close('outside_click');
      return;
    }

    const methodButton = event.target.closest('[data-coffee-quiz-method]');
    const strengthButton = event.target.closest('[data-coffee-quiz-strength]');
    const tasteButton = event.target.closest('[data-coffee-quiz-taste]');
    const previousButton = event.target.closest('[data-coffee-quiz-prev]');

    if (methodButton) {
      this.selected.method = this.resolveMethod(methodButton.dataset.coffeeQuizMethod) || this.selected.method;
      this.emitQuizEvent('answer', { step: 'method', question: 'method', value: this.selected.method });
      this.showStep('strength', { reason: 'answer' });
      return;
    }

    if (strengthButton) {
      this.selected.strength = strengthButton.dataset.coffeeQuizStrength || '';
      this.emitQuizEvent('answer', { step: 'strength', question: 'strength', value: this.selected.strength });
      this.showStep('taste', { reason: 'answer' });
      return;
    }

    if (tasteButton) {
      this.selected.taste = tasteButton.dataset.coffeeQuizTaste || '';
      this.emitQuizEvent('answer', { step: 'taste', question: 'taste', value: this.selected.taste });
      this.showStep('result', { reason: 'answer' });
      return;
    }

    if (previousButton) {
      this.goBack();
    }
  }

  goBack() {
    const current = this.querySelector('[data-coffee-quiz-step]:not([hidden])');
    if (!current) return;

    const currentStep = current.dataset.coffeeQuizStep;
    if (currentStep === 'result') {
      this.emitQuizEvent('back', { fromStep: currentStep, toStep: 'taste' });
      this.showStep('taste', { reason: 'back' });
    } else if (currentStep === 'taste') {
      this.emitQuizEvent('back', { fromStep: currentStep, toStep: 'strength' });
      this.showStep('strength', { reason: 'back' });
    } else if (currentStep === 'strength') {
      this.emitQuizEvent('back', { fromStep: currentStep, toStep: 'method' });
      this.showStep('method', { reason: 'back' });
    }
  }

  showStep(stepName, context = {}) {
    const previousStep = this.dataset.activeStep || '';
    this.dataset.activeStep = stepName;

    this.steps.forEach((step) => {
      step.hidden = step.dataset.coffeeQuizStep !== stepName;
    });

    this.methodLabels.forEach((label) => {
      label.textContent = this.selected.method || this.dataset.defaultMethod || '';
    });
    this.strengthLabels.forEach((label) => {
      label.textContent = this.selected.strength || '';
    });
    this.tasteLabels.forEach((label) => {
      label.textContent = this.selected.taste || '';
    });

    const results = this.updateResults();
    this.resetScrollPosition();
    this.emitQuizEvent('step_view', {
      step: stepName,
      previousStep,
      reason: context.reason || '',
    });
    if (stepName === 'result') {
      this.emitQuizEvent('results_view', {
        resultCount: results.length,
        selectedTags: this.getSelectedTagKeys(),
        products: results,
      });
    }

    const activeStep = this.querySelector('[data-coffee-quiz-step]:not([hidden])');
    if (activeStep) {
      const firstChoice = activeStep.querySelector('button, a');
      if (firstChoice && stepName !== 'result') firstChoice.focus({ preventScroll: true });
    }
  }

  resetScrollPosition() {
    const reset = () => {
      if (this.dialog && typeof this.dialog.scrollTo === 'function') {
        this.dialog.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }

      if (typeof this.scrollTo === 'function') {
        this.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }

      const activeStep = this.querySelector('[data-coffee-quiz-step]:not([hidden])');
      if (activeStep) activeStep.scrollTop = 0;
      if (this.resultsContainer) this.resultsContainer.scrollTop = 0;
    };

    reset();
    requestAnimationFrame(reset);
  }

  updateResults() {
    const method = this.selected.method || this.dataset.defaultMethod || '';
    const selectedTagKeys = this.getSelectedTagKeys();
    const resultLimit = Number.parseInt(this.dataset.resultLimit || '3', 10) || 3;
    const rankedProducts = Array.from(this.resultProducts)
      .map((productCard, index) => {
        const variants = parseQuizVariants(productCard.dataset.coffeeQuizVariants);
        const variant = findQuizVariant(variants, method);

        return {
          productCard,
          index,
          variant,
          score: scoreQuizProduct(productCard, selectedTagKeys),
        };
      })
      .filter(
        (item) =>
          selectedTagKeys.length > 0 &&
          item.score === selectedTagKeys.length &&
          item.variant &&
          item.variant.available
      )
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .slice(0, resultLimit);
    const visibleCards = new Set(rankedProducts.map((item) => item.productCard));
    const visibleVariants = new Map(rankedProducts.map((item) => [item.productCard, item.variant]));

    this.resultProducts.forEach((productCard) => {
      const isVisible = visibleCards.has(productCard);
      productCard.hidden = !isVisible;
      productCard.style.display = isVisible ? '' : 'none';
      productCard.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

      if (isVisible) {
        const variant = visibleVariants.get(productCard);
        delete productCard.dataset.coffeeQuizGrindChosen;
        this.setGrindValidationState(productCard, false);
        this.updateProductCard(productCard, variant ? variant.id : '');
      }
    });

    if (this.emptyResultMessage) {
      this.emptyResultMessage.hidden = visibleCards.size > 0;
    }

    return rankedProducts.map((item) => this.getProductEventData(item.productCard, item.score, item.index));
  }

  getSelectedTagKeys() {
    const tagPrefix = this.dataset.quizTagPrefix || 'quiz_';

    return [this.selected.method, this.selected.strength, this.selected.taste]
      .filter(Boolean)
      .map((value) => normalizeQuizTag(`${tagPrefix}${value}`))
      .filter(Boolean);
  }

  syncMethodsFromFinder() {
    const finderItems = document.querySelectorAll('.coffee-finder-section .finder-item');
    if (!this.methodCardsContainer || !finderItems.length) {
      this.methodButtons = this.querySelectorAll('[data-coffee-quiz-method]');
      return;
    }

    const finderMethods = [];
    const seenMethods = new Set();
    finderItems.forEach((item) => {
      const label = normalizeQuizValue(item.dataset.quizMethod || item.dataset.quiz || getVisibleQuizTriggerLabel(item));
      const icon = item.querySelector('.finder-item-image');
      const key = normalizeMethodKey(label);
      if (!key || seenMethods.has(key)) return;

      seenMethods.add(key);
      finderMethods.push({ label, icon });
    });

    if (!finderMethods.length) {
      this.methodButtons = this.querySelectorAll('[data-coffee-quiz-method]');
      return;
    }

    const templateButton = this.methodButtons[0] || this.methodCardsContainer.querySelector('[data-coffee-quiz-method]');
    const methodButtons = finderMethods.map((method) => this.createMethodButton(method, templateButton));

    this.methodCardsContainer.replaceChildren(...methodButtons);
    this.methodButtons = this.querySelectorAll('[data-coffee-quiz-method]');
  }

  createMethodButton(method, templateButton) {
    const button = templateButton ? templateButton.cloneNode(true) : document.createElement('button');
    button.type = 'button';
    button.className = 'coffee-quiz__card';
    button.dataset.coffeeQuizMethod = method.label;
    button.dataset.coffeeQuizMethodHandle = normalizeMethodHandle(method.label);

    let icon = button.querySelector('[data-coffee-quiz-method-icon]');
    if (!icon) {
      icon = document.createElement('span');
      icon.className = 'coffee-quiz__icon';
      icon.setAttribute('data-coffee-quiz-method-icon', '');
      icon.setAttribute('aria-hidden', 'true');
      button.append(icon);
    }

    if (method.icon) {
      icon.replaceChildren(cloneQuizIcon(method.icon));
    }

    let label = button.querySelector('[data-coffee-quiz-method-card-label]');
    if (!label) {
      label = document.createElement('span');
      label.setAttribute('data-coffee-quiz-method-card-label', '');
      button.append(label);
    }
    label.textContent = method.label;

    return button;
  }

  updateProductCard(productCard, selectedVariantId) {
    const variants = parseQuizVariants(productCard.dataset.coffeeQuizVariants);
    const variant = findVariantById(variants, selectedVariantId);
    const productLink = productCard.querySelector('[data-coffee-quiz-product-link]');
    const productDataCardLink = productCard.querySelector('.grid_products_data_card');
    const variantSelect = productCard.querySelector('[data-coffee-quiz-variant-select]');
    const sizeOptions = productCard.querySelector('[data-coffee-quiz-size-options]');
    const grindControl = productCard.querySelector('[data-coffee-quiz-grind-control]');
    const addButton = productCard.querySelector('[data-coffee-quiz-add]');
    const addButtonText = productCard.querySelector('[data-coffee-quiz-add-text]');
    const price = productCard.querySelector('[data-coffee-quiz-price]');

    if (variant && variant.url && productLink) {
      productLink.href = variant.url;
      if (productDataCardLink) productDataCardLink.href = variant.url;
      productCard.dataset.selectedVariantId = String(variant.id || '');
    } else if (productCard.dataset.productUrl && productLink) {
      productLink.href = productCard.dataset.productUrl;
      if (productDataCardLink) productDataCardLink.href = productCard.dataset.productUrl;
      delete productCard.dataset.selectedVariantId;
    }

    if (variantSelect && variant) {
      const matchingOption = Array.from(variantSelect.options).find((option) => option.value === String(variant.id));
      if (matchingOption) {
        matchingOption.disabled = false;
        variantSelect.value = String(variant.id);
      }
    }

    if (price && variant && variant.formattedPrice) {
      price.textContent = variant.formattedPrice;
    }

    if (sizeOptions) {
      this.renderSizeOptions(productCard, variants, variant);
    }

    if (grindControl) {
      this.renderGrindOptions(productCard, variants, variant);
    }

    if (addButton) {
      const needsGrindSelection = Boolean(
        grindControl && !grindControl.hidden && productCard.dataset.coffeeQuizGrindChosen !== 'true'
      );
      const variantAvailable = Boolean(variant && variant.available);
      const canAdd = variantAvailable && !needsGrindSelection;
      addButton.disabled = !variantAvailable;
      addButton.setAttribute('aria-disabled', canAdd ? 'false' : 'true');
      if (addButtonText) addButtonText.textContent = variantAvailable ? getAddToCartText() : getSoldOutText();
    }
  }

  setGrindValidationState(productCard, showError) {
    if (!productCard) return;

    const grindSelect = productCard.querySelector('[data-coffee-quiz-grind-select]');
    const errorMessage = productCard.querySelector('[data-coffee-quiz-grind-error]');

    if (grindSelect) {
      if (showError) {
        grindSelect.setAttribute('aria-invalid', 'true');
      } else {
        grindSelect.removeAttribute('aria-invalid');
      }
    }

    if (errorMessage) errorMessage.hidden = !showError;
  }

  renderSizeOptions(productCard, variants, selectedVariant) {
    const sizeOptions = productCard.querySelector('[data-coffee-quiz-size-options]');
    const variantSelect = productCard.querySelector('[data-coffee-quiz-variant-select]');
    if (!sizeOptions) return;

    const method = getQuizGrindOption(selectedVariant) || getQuizSelectedGrind(productCard) || this.selected.method || this.dataset.defaultMethod || '';
    const methodVariants = getQuizMethodVariants(variants, method);
    const sizes = getQuizSizeOptions(methodVariants, method);

    sizeOptions.replaceChildren();
    sizeOptions.classList.remove('coffee-quiz-product__sizes--single', 'coffee-quiz-product__sizes--multiple');

    if (!sizes.length) {
      sizeOptions.hidden = true;
      return;
    }

    const selectedSizeKey = normalizeQuizSizeKey(getQuizSizeOption(selectedVariant, method));
    const legend = document.createElement('legend');
    legend.className = 'visually-hidden';
    legend.textContent = 'בחר גודל';
    sizeOptions.append(legend);

    if (sizes.length === 1) {
      sizeOptions.classList.add('coffee-quiz-product__sizes--single');
      const size = sizes[0];
      const label = document.createElement('span');
      label.className = 'coffee-quiz-product__size coffee-quiz-product__size--static';
      label.dataset.coffeeQuizSizeValue = size.label;

      const text = document.createElement('span');
      text.textContent = size.label;

      label.append(text);
      sizeOptions.append(label);
      sizeOptions.hidden = false;
      return;
    }

    sizeOptions.classList.add('coffee-quiz-product__sizes--multiple');

    sizes.forEach((size, index) => {
      const label = document.createElement('label');
      label.className = 'coffee-quiz-product__size';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `${variantSelect ? variantSelect.id : 'coffee-quiz-size'}-size`;
      input.value = size.label;
      input.disabled = !size.available;
      input.checked = selectedSizeKey ? size.key === selectedSizeKey : index === 0;
      input.setAttribute('data-coffee-quiz-size-input', '');

      const text = document.createElement('span');
      text.textContent = size.label;

      label.append(input, text);
      sizeOptions.append(label);
    });

    sizeOptions.hidden = false;
  }

  renderGrindOptions(productCard, variants, selectedVariant) {
    const grindControl = productCard.querySelector('[data-coffee-quiz-grind-control]');
    const grindSelect = productCard.querySelector('[data-coffee-quiz-grind-select]');
    if (!grindControl || !grindSelect) return;

    const selectedSize = getQuizSizeOption(selectedVariant);
    const grinds = getQuizGrindOptions(variants, selectedSize);
    const selectedGrindKey = normalizeMethodKey(getQuizGrindOption(selectedVariant));
    const preferredGrindKey = normalizeMethodKey(this.selected.method || this.dataset.defaultMethod || '');
    const hasChosenGrind = productCard.dataset.coffeeQuizGrindChosen === 'true';

    grindSelect.replaceChildren();

    if (!grinds.length) {
      grindSelect.disabled = true;
      grindControl.hidden = true;
      return;
    }

    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = grindSelect.dataset.placeholder || 'סוג טחינה';
    placeholderOption.disabled = true;
    placeholderOption.selected = !hasChosenGrind;
    grindSelect.append(placeholderOption);

    grinds.forEach((grind) => {
      const option = document.createElement('option');
      option.value = grind.label;
      option.textContent = grind.label;
      option.disabled = !grind.available;
      option.selected = hasChosenGrind && (selectedGrindKey ? grind.key === selectedGrindKey : grind.key === preferredGrindKey);
      grindSelect.append(option);
    });

    if (!hasChosenGrind) {
      grindSelect.value = '';
    } else if (!grindSelect.value) {
      const preferredOption = Array.from(grindSelect.options).find((option) => {
        return normalizeMethodKey(option.value) === preferredGrindKey && !option.disabled;
      });
      const fallbackOption = Array.from(grindSelect.options).find((option) => !option.disabled) || grindSelect.options[0];
      if (preferredOption || fallbackOption) grindSelect.value = (preferredOption || fallbackOption).value;
    }

    grindSelect.disabled = false;
    grindControl.hidden = false;
  }

  resolveMethod(method) {
    const requestedMethod = normalizeQuizValue(method);
    if (!requestedMethod) return '';

    const requestedKey = normalizeMethodKey(requestedMethod);
    const requestedHandle = normalizeMethodHandle(requestedMethod);
    const methodButton = Array.from(this.methodButtons).find((button) => {
      const label = button.dataset.coffeeQuizMethod || '';
      const handle = button.dataset.coffeeQuizMethodHandle || '';

      return (
        normalizeMethodKey(label) === requestedKey ||
        normalizeMethodHandle(label) === requestedHandle ||
        normalizeMethodKey(handle) === requestedKey ||
        normalizeMethodHandle(handle) === requestedHandle
      );
    });

    return methodButton ? methodButton.dataset.coffeeQuizMethod : requestedMethod;
  }

  getSelectedState() {
    return {
      method: this.selected.method || this.dataset.defaultMethod || '',
      strength: this.selected.strength || '',
      taste: this.selected.taste || '',
      tags: this.getSelectedTagKeys(),
    };
  }

  getProductEventData(productCard, score, index) {
    if (!productCard) return null;

    const productLink = productCard.querySelector('[data-coffee-quiz-product-link]');
    const selectedVariantId =
      productCard.dataset.selectedVariantId ||
      productCard.querySelector('[data-coffee-quiz-variant-select]')?.value ||
      '';

    return {
      id: productCard.dataset.productId || '',
      title:
        productCard.dataset.productTitle ||
        productCard.querySelector('.coffee-quiz-product__title')?.textContent?.trim() ||
        '',
      handle: productCard.dataset.productHandle || '',
      url: productLink ? productLink.href : productCard.dataset.productUrl || '',
      selectedVariantId: String(selectedVariantId || ''),
      score: typeof score === 'number' ? score : undefined,
      position: typeof index === 'number' ? index + 1 : undefined,
    };
  }

  getVariantEventData(variant) {
    if (!variant) return null;

    return {
      id: String(variant.id || ''),
      title: variant.title || '',
      url: variant.url || '',
      price: Number(variant.price || 0),
      formattedPrice: variant.formattedPrice || '',
      available: Boolean(variant.available),
      options: Array.isArray(variant.options) ? variant.options : [],
    };
  }

  emitQuizEvent(action, detail = {}) {
    const eventName = action.startsWith('coffee-quiz:') ? action : `coffee-quiz:${action}`;
    const payload = {
      quizId: this.id || '',
      sectionId: this.dataset.sectionId || '',
      activeStep: this.dataset.activeStep || '',
      selected: this.getSelectedState(),
      ...detail,
    };

    this.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        composed: true,
        detail: payload,
      })
    );

    pushQuizDataLayerEvent(eventName, payload);
  }
}

if (!customElements.get('coffee-quiz')) {
  customElements.define('coffee-quiz', CoffeeQuiz);
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('a, [data-coffee-quiz-trigger]');
  if (!trigger) return;

  const linkData = getQuizLinkData(trigger);
  if (!linkData.isQuizTrigger) return;
  if (!linkData.shouldOpenHere) return;

  const quiz = document.querySelector('coffee-quiz');
  if (!quiz) return;

  event.preventDefault();

  const activeDrawer = document.querySelector('cart-drawer.active');
  if (activeDrawer && typeof activeDrawer.close === 'function') activeDrawer.close();

  const triggerData = getQuizTriggerEventData(trigger, linkData);
  if (typeof quiz.emitQuizEvent === 'function') {
    quiz.emitQuizEvent('trigger', {
      method: linkData.method || '',
      trigger: triggerData,
    });
  }

  quiz.open(linkData.method, triggerData);
});

function openQuizFromLocation() {
  const linkData = getQuizLinkData(window.location);
  if (!linkData.isQuizTrigger) return;

  const quiz = document.querySelector('coffee-quiz');
  if (!quiz) return;

  quiz.open(linkData.method, {
    type: 'url',
    href: window.location.href,
    method: linkData.method || '',
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', openQuizFromLocation);
} else {
  openQuizFromLocation();
}

window.addEventListener('hashchange', openQuizFromLocation);

function getQuizLinkData(source) {
  const hasDataTrigger = source instanceof Element && source.hasAttribute('data-coffee-quiz-trigger');
  const visibleLabel = source instanceof Element ? getVisibleQuizTriggerLabel(source) : '';
  const rawDataMethod = source instanceof Element ? source.dataset.quizMethod || source.dataset.quiz || '' : '';
  const dataMethod = isQuizBlockId(rawDataMethod) ? visibleLabel : rawDataMethod || visibleLabel;
  const href = source instanceof Location ? source.href : source.getAttribute('href') || '';
  const url = getQuizUrl(href);
  const hashData = url ? getQuizHashData(url.hash) : getQuizHashData(href);
  const queryData = url ? getQuizQueryData(url.searchParams) : { isQuizQuery: false, method: '' };
  const isSamePage =
    !url ||
    (url.origin === window.location.origin &&
      url.pathname === window.location.pathname &&
      (href.startsWith('#') || url.search === window.location.search || queryData.isQuizQuery));
  const isQuizTrigger = hasDataTrigger || hashData.isQuizHash || queryData.isQuizQuery;
  const method = dataMethod || hashData.method || queryData.method;

  return {
    isQuizTrigger,
    shouldOpenHere: hasDataTrigger || isSamePage,
    method: isGenericQuizLabel(method) || isQuizBlockId(method) ? '' : method,
  };
}

function getQuizUrl(href) {
  if (!href) return null;

  try {
    return new URL(href, window.location.href);
  } catch (error) {
    return null;
  }
}

function getQuizTriggerEventData(trigger, linkData) {
  if (!(trigger instanceof Element)) {
    return {
      type: 'unknown',
      method: linkData.method || '',
    };
  }

  return {
    type: trigger.hasAttribute('data-coffee-quiz-trigger') ? 'data_trigger' : 'link',
    text: trigger.textContent ? trigger.textContent.trim().replace(/\s+/g, ' ') : '',
    href: trigger.getAttribute('href') || '',
    method: linkData.method || '',
    id: trigger.id || '',
    classes: trigger.className || '',
  };
}

function getQuizHashData(hash) {
  const value = (hash || '').replace(/^#/, '');
  if (!/^(quiz|coffee-quiz|coffeequiz)(?:$|[-/:=?])/.test(value)) return { isQuizHash: false, method: '' };

  const queryIndex = value.indexOf('?');
  if (queryIndex !== -1) {
    const params = new URLSearchParams(value.slice(queryIndex + 1));
    return {
      isQuizHash: true,
      method: getMethodFromParams(params),
    };
  }

  const match = value.match(/^(?:quiz|coffee-quiz|coffeequiz)(?:[-/:=](.+))?$/);
  return {
    isQuizHash: true,
    method: match ? normalizeQuizValue(match[1]) : '',
  };
}

function getQuizQueryData(params) {
  const method = getMethodFromParams(params);

  return {
    isQuizQuery: params.has('quiz') || params.has('quiz_method') || params.has('coffee_quiz'),
    method,
  };
}

function getMethodFromParams(params) {
  return normalizeQuizValue(params.get('method') || params.get('quiz_method') || params.get('coffee_quiz') || params.get('quiz') || '');
}

function scoreQuizProduct(productCard, selectedTagKeys) {
  if (!selectedTagKeys.length) return 0;

  const productTagKeys = parseQuizTags(productCard.dataset.coffeeQuizTags);
  if (!productTagKeys.length) return 0;

  return selectedTagKeys.reduce((score, tagKey) => (productTagKeys.includes(tagKey) ? score + 1 : score), 0);
}

function parseQuizTags(rawTags) {
  if (!rawTags) return [];

  return rawTags
    .split('||')
    .map((tag) => normalizeQuizTag(tag))
    .filter(Boolean);
}

function parseQuizVariants(rawVariants) {
  if (!rawVariants) return [];

  try {
    const variants = JSON.parse(rawVariants);
    return Array.isArray(variants) ? variants : [];
  } catch (error) {
    return [];
  }
}

function findQuizVariant(variants, method, selectedSize) {
  if (!variants.length) return null;

  const candidates = getQuizMethodVariants(variants, method);
  const selectedSizeKey = normalizeQuizSizeKey(selectedSize);
  const sizeMatches = selectedSizeKey
    ? candidates.filter((variant) => normalizeQuizSizeKey(getQuizSizeOption(variant, method)) === selectedSizeKey)
    : [];
  const preferredCandidates = sizeMatches.length ? sizeMatches : candidates;

  return (
    preferredCandidates.find((variant) => variant.available && hasPreferredQuizSize(variant, method)) ||
    preferredCandidates.find((variant) => variant.available) ||
    preferredCandidates.find((variant) => hasPreferredQuizSize(variant, method)) ||
    preferredCandidates[0] ||
    null
  );
}

function getQuizMethodVariants(variants, method) {
  const methodKey = normalizeMethodKey(method);
  const methodMatches = methodKey
    ? variants.filter((variant) => getQuizOptionParts(variant).some((option) => isQuizMethodOption(option, methodKey)))
    : [];

  return methodMatches.length ? methodMatches : variants;
}

function getQuizSizeOptions(variants, method) {
  const sizes = new Map();

  variants.forEach((variant) => {
    const label = getQuizSizeOption(variant, method);
    const key = normalizeQuizSizeKey(label);
    if (!key) return;

    const existing = sizes.get(key);
    if (existing) {
      existing.available = existing.available || Boolean(variant.available);
      return;
    }

    sizes.set(key, {
      key,
      label,
      available: Boolean(variant.available),
    });
  });

  return Array.from(sizes.values());
}

function getQuizGrindOptions(variants, selectedSize) {
  const selectedSizeKey = normalizeQuizSizeKey(selectedSize);
  const grinds = new Map();

  variants.forEach((variant) => {
    if (selectedSizeKey && normalizeQuizSizeKey(getQuizSizeOption(variant)) !== selectedSizeKey) return;

    const label = getQuizGrindOption(variant);
    const key = normalizeMethodKey(label);
    if (!key || isDefaultQuizOption(label)) return;

    const existing = grinds.get(key);
    if (existing) {
      existing.available = existing.available || Boolean(variant.available);
      return;
    }

    grinds.set(key, {
      key,
      label,
      available: Boolean(variant.available),
    });
  });

  return Array.from(grinds.values());
}

function findVariantById(variants, variantId) {
  const selectedId = String(variantId || '');
  if (!selectedId) return null;

  return variants.find((variant) => String(variant.id || '') === selectedId) || null;
}

function getVariantOptions(variant) {
  return Array.isArray(variant.options) ? variant.options : [];
}

function getQuizOptionParts(variant) {
  if (!variant) return [];

  const optionParts = getVariantOptions(variant)
    .flatMap((option) => splitQuizVariantTitle(option))
    .filter(Boolean);

  if (optionParts.length) return optionParts;

  return splitQuizVariantTitle(variant.title || '');
}

function getQuizSizeOption(variant, method) {
  if (!variant) return '';

  const options = getQuizOptionParts(variant);
  const methodKey = normalizeMethodKey(method);
  const sizeOption =
    options.find((option) => isQuizSizeOption(option)) ||
    splitQuizVariantTitle(variant.title).find((option) => isQuizSizeOption(option));

  if (sizeOption) return extractQuizSizeLabel(sizeOption);

  const nonMethodOption = methodKey ? options.find((option) => !isQuizMethodOption(option, methodKey)) : options[0];
  return extractQuizSizeLabel(nonMethodOption || variant.title || '');
}

function getQuizGrindOption(variant) {
  if (!variant) return '';

  const grindOption = getQuizOptionParts(variant).find((option) => !isQuizSizeOption(option) && !isDefaultQuizOption(option));
  return normalizeQuizValue(grindOption || '');
}

function isQuizMethodOption(option, methodKey) {
  const optionKey = normalizeMethodKey(option);
  return optionKey === methodKey || optionKey.includes(methodKey) || methodKey.includes(optionKey);
}

function isQuizSizeOption(value) {
  return /(?:גרם|ג׳|ג'|קג|ק״ג|ק"ג|קילו|kg|kilo|gram|grams|\bg\b)/i.test(normalizeQuizValue(value));
}

function extractQuizSizeLabel(value) {
  const label = normalizeQuizValue(value);
  const matchingPart = splitQuizVariantTitle(label).find((part) => isQuizSizeOption(part));
  return matchingPart || label;
}

function splitQuizVariantTitle(value) {
  return normalizeQuizValue(value)
    .split(/\s*(?:\/|•|\||-)\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function hasPreferredQuizSize(variant, method) {
  return /(^|[^0-9])250([^0-9]|$)/.test(normalizeQuizValue(getQuizSizeOption(variant, method)));
}

function getQuizSelectedSize(productCard) {
  const checkedSize = productCard.querySelector('[data-coffee-quiz-size-input]:checked');
  if (checkedSize) return checkedSize.value || '';

  const staticSize = productCard.querySelector('[data-coffee-quiz-size-value]');
  return staticSize ? staticSize.dataset.coffeeQuizSizeValue || '' : '';
}

function getQuizSelectedGrind(productCard) {
  const grindSelect = productCard.querySelector('[data-coffee-quiz-grind-select]');
  return grindSelect ? grindSelect.value || '' : '';
}

function isDefaultQuizOption(value) {
  const option = normalizeQuizValue(value).toLowerCase();
  return !option || option === 'default title' || option === 'title';
}

function normalizeQuizSizeKey(value) {
  return normalizeQuizValue(value)
    .toLowerCase()
    .replace(/[׳'’"`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isGenericQuizLabel(value) {
  const label = normalizeQuizValue(value);
  return !label || label === 'התאימו לי קפה' || label === 'כן! לשאלון';
}

function isQuizBlockId(value) {
  return /^qbc-[a-z0-9]+$/i.test(normalizeQuizValue(value));
}

function getVisibleQuizTriggerLabel(trigger) {
  const label = trigger.querySelector('.finder-item-label');
  return normalizeQuizValue(label ? label.textContent : '');
}

function cloneQuizIcon(sourceIcon) {
  const iconClone = sourceIcon.cloneNode(true);
  iconClone.removeAttribute('class');
  iconClone.setAttribute('aria-hidden', 'true');

  if (iconClone.tagName && iconClone.tagName.toLowerCase() === 'img') {
    iconClone.alt = '';
  }

  return iconClone;
}

function pushQuizDataLayerEvent(eventName, payload) {
  if (!Array.isArray(window.dataLayer)) return;

  window.dataLayer.push({
    event: eventName.replace(/[:-]+/g, '_'),
    coffeeQuiz: payload,
  });
}

function getAddToCartText() {
  return window.variantStrings && window.variantStrings.addToCart ? window.variantStrings.addToCart : 'Add to cart';
}

function getSoldOutText() {
  return window.variantStrings && window.variantStrings.soldOut ? window.variantStrings.soldOut : 'Sold out';
}

function normalizeQuizValue(value) {
  if (!value) return '';

  try {
    return decodeURIComponent(String(value).replace(/\+/g, ' ')).trim();
  } catch (error) {
    return String(value).trim();
  }
}

function normalizeMethodKey(value) {
  return normalizeQuizValue(value)
    .toLowerCase()
    .replace(/[׳'’"`]/g, '')
    .replace(/[_\-\s]+/g, ' ')
    .trim();
}

function normalizeMethodHandle(value) {
  return normalizeQuizValue(value)
    .toLowerCase()
    .replace(/[׳'’"`]/g, '')
    .replace(/[^a-z0-9\u0590-\u05ff]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function normalizeQuizTag(value) {
  return normalizeQuizValue(value)
    .toLowerCase()
    .replace(/[׳'’"`]/g, '')
    .replace(/[\s\-_]+/g, '_')
    .replace(/^_+|_+$/g, '');
}
